import {
  executionDetailSchema,
  isTerminalExecutionStatus,
  listRunbooksOutputSchema,
  runbookContextSchema,
  runbookExecutionEventSchema,
  runbookExecutionRequestSchema,
  runbookExecutionResultSchema,
  type RunbookContextV1,
  type RunbookExecutionEvent,
  type RunbookExecutionRecord,
  type RunbookExecutionRequest,
  type RunbookExecutionResult,
  type RunbookGateway,
  type RunbookRecord,
} from "@bitsentry-ce/core/features/runbooks";

const executionReferenceSchema = runbookExecutionEventSchema.shape.executionId;
const incidentIdSchema = runbookExecutionEventSchema.shape.incidentId.unwrap();

export interface CloudRunbookGatewayTransport {
  listExecutable(): Promise<RunbookRecord[]>;
  getRunbookContext(runbookId: string): Promise<RunbookContextV1 | null>;
  start(request: RunbookExecutionRequest): Promise<RunbookExecutionResult>;
  get(executionId: string): Promise<RunbookExecutionRecord | null>;
  getLatestForIncidentThread(
    incidentId: string,
  ): Promise<RunbookExecutionRecord | null>;
  subscribe(listener: (event: RunbookExecutionEvent) => void): () => void;
  cancel(executionId: string): Promise<void>;
}

function wait(delayMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(new DOMException("Runbook wait aborted", "AbortError"));
      return;
    }

    const handleAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Runbook wait aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, delayMs);
    signal?.addEventListener("abort", handleAbort, { once: true });
  });
}

function toReplayEvent(
  incidentId: string,
  execution: RunbookExecutionRecord,
): RunbookExecutionEvent {
  return runbookExecutionEventSchema.parse({
    eventId: `snapshot:${execution.executionId}:${String(execution.snapshotVersion ?? 0)}`,
    occurredAt:
      execution.completedAt ?? execution.lastActivityAt ?? execution.startedAt,
    // Cloud execution and result IDs are intentionally identical. This keeps
    // replay possible through the persisted snapshot read endpoint.
    resultId: execution.executionId,
    executionId: execution.executionId,
    incidentId,
    execution,
  });
}

/**
 * Product-neutral RunbookGateway implementation for the Dashboard transport.
 * The transport stays authenticated and Cloud-specific; callers only see the
 * shared serializable runbook contract.
 */
export function createCloudRunbookGateway(
  transport: CloudRunbookGatewayTransport,
): RunbookGateway {
  return {
    async listExecutable() {
      return listRunbooksOutputSchema.parse(await transport.listExecutable());
    },
    async getRunbookContext(runbookId) {
      const context = await transport.getRunbookContext(
        executionReferenceSchema.parse(runbookId),
      );
      if (context === null) {
        throw new Error(`Runbook context not found: ${runbookId}`);
      }
      return runbookContextSchema.parse(context);
    },
    async start(request) {
      return runbookExecutionResultSchema.parse(
        await transport.start(runbookExecutionRequestSchema.parse(request)),
      );
    },
    async get(executionId) {
      const execution = await transport.get(
        executionReferenceSchema.parse(executionId),
      );
      return execution === null ? null : executionDetailSchema.parse(execution);
    },
    async getLatestForIncidentThread(incidentId) {
      const execution = await transport.getLatestForIncidentThread(
        incidentIdSchema.parse(incidentId),
      );
      return execution === null ? null : executionDetailSchema.parse(execution);
    },
    async waitForCompletion(executionId, options) {
      const validatedExecutionId = executionReferenceSchema.parse(executionId);
      const pollIntervalMs = options?.pollIntervalMs ?? 500;
      const deadline =
        options?.timeoutMs === undefined
          ? undefined
          : Date.now() + options.timeoutMs;

      while (true) {
        options?.signal?.throwIfAborted();
        const execution = await transport.get(validatedExecutionId);
        options?.signal?.throwIfAborted();
        if (execution === null || isTerminalExecutionStatus(execution.status)) {
          return execution === null
            ? null
            : executionDetailSchema.parse(execution);
        }
        if (deadline !== undefined && Date.now() >= deadline) {
          return executionDetailSchema.parse(execution);
        }
        await wait(pollIntervalMs, options?.signal);
      }
    },
    subscribe(incidentId, listener) {
      const validatedIncidentId = incidentIdSchema.parse(incidentId);
      let active = true;
      let receivedLiveEvent = false;
      const unsubscribe = transport.subscribe((rawEvent) => {
        const event = runbookExecutionEventSchema.parse(rawEvent);
        if (active && event.incidentId === validatedIncidentId) {
          receivedLiveEvent = true;
          listener(event);
        }
      });

      // The first event is a durable snapshot, so a restarted browser can
      // render the current state before receiving subsequent websocket events.
      void transport
        .getLatestForIncidentThread(validatedIncidentId)
        .then((execution) => {
          if (active && !receivedLiveEvent && execution !== null) {
            listener(toReplayEvent(validatedIncidentId, execution));
          }
        })
        .catch(() => {
          // Keep the live subscription active if history cannot be replayed.
        });

      return () => {
        active = false;
        unsubscribe();
      };
    },
    cancel(executionId) {
      return transport.cancel(executionReferenceSchema.parse(executionId));
    },
  };
}
